<template>
  <details
    ref="details"
    class="tapp-accordion"
    :class="{ 'tapp-accordion--borders': bordersVisible, 'tapp-accordion--animated': animate }"
    :open="openOnInit || visible"
    data-testid="tapp-accordion"
  >
    <summary
      ref="summary"
      class="tapp-accordion__button"
      :class="{
        h5: !['small', 'medium'].includes(variant),
        'tapp-accordion__button--small': variant === 'small',
        'tapp-accordion__button--horizontal-padding': horizontalPadding,
        'tapp-accordion__button--primary-color': buttonTextColor === 'primary',
      }"
      v-bind="attrsButtonMerged"
      data-testid="tapp-accordion-button"
      @click="handleClick"
    >
      <span class="tapp-accordion__title">
        {{ title }}
      </span>

      <span class="tapp-accordion__icon">
        <span>
          <svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M1 1L7 7L13 1"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
      </span>
    </summary>

    <div v-show="animate || visible" ref="panel" class="tapp-accordion__panel" data-testid="tapp-accordion-panel">
      <div
        class="tapp-accordion__inner"
        :class="{
          'tapp-accordion__inner--small': variant === 'small',
          'tapp-accordion__inner--horizontal-padding': horizontalPadding,
        }"
        data-testid="tapp-accordion-inner"
      >
        <slot />
      </div>
    </div>
  </details>
</template>

<script lang="ts">
  export const variants = ['default', 'small', 'faq'] as const;
  export const buttonTextColors = ['default', 'primary'] as const;
</script>

<script lang="ts" setup>
  const props = withDefaults(
    defineProps<{
      /**
       * Attributes for analytics
       */
      attrsButton?: DataAnalyticsInterface;
      attrsButtonOpen?: DataAnalyticsInterface;
      attrsButtonClosed?: DataAnalyticsInterface;

      animate?: boolean;

      /**
       * If true, shows a border at the bottom of the accordion
       */
      bordersVisible?: boolean;

      /**
       * Control horizontal padding at the edges
       */
      horizontalPadding?: boolean;

      /**
       * Controls if the accordion should be open on init
       */
      openOnInit?: boolean;

      /**
       * Controls if the accordion should close based on other accordion open click.
       */
      useVisible?: boolean;

      /**
       * The title for this accordion item
       */
      title: string;

      /**
       * Options for text colors on the main button
       */
      buttonTextColor?: (typeof buttonTextColors)[number];

      /**
       * Variant options controlling styles
       */
      variant?: (typeof variants)[number];
    }>(),
    {
      attrsButton: undefined,
      attrsButtonOpen: undefined,
      attrsButtonClosed: undefined,
      buttonTextColor: 'default',
      horizontalPadding: false,
      variant: variants[0],
    },
  );

  const index = ref<number | null>(null);
  const isOpen = ref<boolean>(false);
  const details = ref<HTMLDetailsElement>();

  const accordion = inject<Ref<{ count: number; active?: number | null }> | null>('accordion', null);

  const attrsButtonMerged = computed(() => {
    return {
      ...props.attrsButton,
      ...(isOpen.value && props.attrsButtonOpen),
      ...(!isOpen.value && props.attrsButtonClosed),
    };
  });

  const visible = computed(() => {
    // if useVisible === false accordion will not close by each other.
    if (props.useVisible) {
      // if the parent accordion doesn't exist, then use local isOpen.
      return accordion ? index.value === accordion.value.active : isOpen.value;
    }
    return isOpen.value;
  });

  init();

  function init() {
    // set index of accordion when the parent accordion is available
    if (accordion) {
      index.value = accordion.value.count++;
    }

    // set isOpen to true if the parent accordion is not available
    if (!accordion && props.openOnInit) {
      isOpen.value = true;
    }

    // set index to parent component when available and isOpen to true;
    if (accordion && props.openOnInit) {
      accordion.value.active = index.value;
      isOpen.value = true;
    }
  }

  function handleClick(e: Event) {
    // return if animation is not supported
    e.preventDefault();
    toggle();
  }

  function toggle() {
    // Check if the element is being closed or is already closed
    if (details.value?.open) {
      details.value.open = false;
      isOpen.value = false;

      if (accordion && accordion.value.active === index.value) {
        accordion.value.active = null;
      }

      return;
    }

    if (details.value) {
      details.value.open = true;
    }

    isOpen.value = true;

    if (accordion) {
      accordion.value.active = index.value;
    }
  }

  // Expose the toggle function to parent components
  defineExpose({
    toggle,
  });
</script>

<style lang="postcss">
  .tapp-accordion[open] > summary .tapp-accordion__icon {
    transform: scaleY(-1);
  }

  .tapp-accordion {
    box-sizing: border-box;
    font: var(--typography-large-body-base);

    summary::-webkit-details-marker {
      display: none;
    }
  }

  .tapp-accordion__title {
    font: var(--typography-large-body-lg-highlight);
  }

  .tapp-accordion__button::marker {
    display: none;
  }

  .tapp-accordion__icon {
    position: relative;
    bottom: -0.2ex;
    transform-origin: center center;

    display: flex;
    align-items: center;

    transition: transform 0.15s ease-in-out;
  }

  .tapp-accordion__button {
    cursor: pointer;

    display: grid;
    grid-template-columns: 1fr auto;
    gap: var(--spacing-4);

    box-sizing: border-box;
    width: 100%;
    padding: var(--spacing-4) 0;

    color: inherit;
    text-align: left;

    outline: 2px solid transparent;
    outline-offset: var(--spacing-1);

    transition: outline-offset 200ms ease-in-out;

    &:hover {
      background: var(--colors-warm-600);
    }

    &:focus-visible {
      outline-color: var(--outline-border-color);
      outline-width: 2px;
      outline-offset: 2px;
    }

    .tapp-accordion--is-panel-open {
      padding: var(--spacing-4) 0;

      .tapp-accordion__icon {
        transform: rotate(180deg);
      }
    }

    .tapp-accordion--horizontal-padding {
      padding-right: var(--spacing-4);
      padding-left: var(--spacing-4);
    }

    .tapp-accordion--small {
      padding: var(--spacing-2) 0;

      &.tapp-accordion__button {
        &--horizontal-padding {
          padding: var(--spacing-2);
        }
      }
    }

    .tapp-accordion--primary-color {
      color: var(--link-text-color);
    }

    .tapp-accordion__panel {
      transition: 0.25s ease-in-out;
    }

    .tapp-accordion__inner {
      max-width: 55rem;
      padding: 0 0 var(--spacing-4);

      .tapp-accordion--horizontal-padding {
        padding-right: var(--spacing-4);
        padding-left: var(--spacing-4);
      }

      .tapp-accordion--small {
        padding: 0 0 var(--spacing-2);

        &.tapp-accordion__inner {
          &--horizontal-padding {
            padding: 0 var(--spacing-2) var(--spacing-2);
          }
        }
      }
    }

    .tapp-accordion--borders {
      border-bottom: 1px solid var(--colors-gray-400);

      &:first-child {
        border-top: 1px solid var(--colors-gray-400);
      }
    }
  }

  .slide-enter-active {
    transition-timing-function: ease-in;
    transition-duration: 0.1s;
  }

  .slide-leave-active {
    transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
    transition-duration: 0.1s;
  }

  .slide-enter-to,
  .slide-leave {
    overflow: hidden;
    max-height: 100px;
  }

  .slide-enter,
  .slide-leave-to {
    overflow: hidden;
    max-height: 0;
  }
</style>

<style lang="postcss" scoped>
  .tapp-accordion--animated {
    @media (prefers-reduced-motion: no-preference) {
      @supports selector(::details-content) and (interpolate-size: allow-keywords) and
        (transition-behavior: allow-discrete) {
        &::details-content {
          overflow: clip;
          overflow-clip-margin: var(--spacing-1);
          display: flow-root;

          interpolate-size: allow-keywords;
          height: 0;

          opacity: 0;

          transition:
            height 200ms ease-out,
            opacity 200ms ease-out,
            content-visibility 200ms allow-discrete;
        }

        &[open]::details-content {
          height: auto;
          opacity: 1;
        }
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .tapp-accordion__icon {
        transition: none;
      }
    }
  }
</style>
